import React, { useRef, useState } from "react";
import { Upload, X, Loader2 } from "lucide-react";
import { uploadToCloudinary } from "@/lib/cloudinary";

export default function PhotoUploader({ folder = "profiles/", multiple = true, accept = "image", onUploaded, testid = "photo-uploader", anonymous = false }) {
  const ref = useRef(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [previews, setPreviews] = useState([]);

  const acceptMime = accept === "video"
    ? "video/mp4,video/quicktime,video/webm"
    : accept === "image-or-video"
    ? "image/jpeg,image/png,image/webp,video/mp4,video/quicktime,video/webm"
    : "image/jpeg,image/png,image/webp";

  const handleFiles = async (e) => {
    setError("");
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setBusy(true);
    try {
      const urls = [];
      for (const f of files) {
        const resource_type = f.type.startsWith("video/") ? "video" : "image";
        const { secure_url } = await uploadToCloudinary(f, folder, resource_type, anonymous);
        urls.push(secure_url);
        setPreviews((p) => [...p, { url: secure_url, type: resource_type }]);
      }
      onUploaded && onUploaded(multiple ? urls : urls[0]);
    } catch (e) {
      setError(e.message || "Error al subir");
    } finally {
      setBusy(false);
      if (ref.current) ref.current.value = "";
    }
  };

  const hint = accept === "video" ? "MP4, MOV o WEBM · máximo 100 MB"
              : accept === "image-or-video" ? "JPG, PNG, WEBP, MP4 · máx 100 MB"
              : "JPG, PNG o WEBP · máximo 10 MB";
  const label = accept === "video" ? "Haz clic o arrastra un video"
              : "Haz clic o arrastra una foto";

  return (
    <div data-testid={testid}>
      <div
        onClick={() => ref.current?.click()}
        className="flex flex-col items-center justify-center gap-2 cursor-pointer transition-colors p-8 text-center"
        style={{
          background: "var(--bg-surface)",
          border: "1px dashed var(--border-accent)",
          borderRadius: "2px",
        }}
        onMouseEnter={(e) => e.currentTarget.style.background = "var(--bg-elevated)"}
        onMouseLeave={(e) => e.currentTarget.style.background = "var(--bg-surface)"}
        data-testid={`${testid}-dropzone`}
      >
        {busy ? (
          <Loader2 size={28} strokeWidth={1.2} className="animate-spin" color="var(--brand-primary)" />
        ) : (
          <Upload size={28} strokeWidth={1.2} color="var(--brand-primary)" />
        )}
        <p className="text-sm" style={{ color: "var(--text-primary)" }}>
          {busy ? "Subiendo…" : label}
        </p>
        <p className="text-xs" style={{ color: "var(--text-muted)" }}>
          {hint} {multiple ? "· varias a la vez" : ""}
        </p>
      </div>
      <input
        ref={ref}
        type="file"
        accept={acceptMime}
        multiple={multiple}
        onChange={handleFiles}
        className="hidden"
        data-testid={`${testid}-input`}
      />
      {error && <p className="text-xs mt-2" style={{ color: "#ef6d6d" }} data-testid={`${testid}-error`}>{error}</p>}

      {previews.length > 0 && (
        <div className="grid grid-cols-3 sm:grid-cols-4 gap-2 mt-3" data-testid={`${testid}-previews`}>
          {previews.map((p, i) => (
            <div key={i} className="relative" style={{ aspectRatio: "3/4", background: "var(--bg-elevated)" }}>
              {p.type === "video"
                ? <video src={p.url} className="w-full h-full object-cover" controls muted />
                : <img src={p.url} alt="" className="w-full h-full object-cover" />}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
